import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { deleteSession, updateInsectData, deleteSessionRecord } from "../redux/slices/dataSlice";
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface ProbeDataSessionProps {
  sessionPageNum: number;
  setSessionPageNum: (page: number) => void;
  resetSessionPage: () => void;
}

export default function ProbeDataSession({ 
  sessionPageNum, 
  setSessionPageNum, 
  resetSessionPage 
}: ProbeDataSessionProps) {
  const { t, i18n } = useTranslation(['session', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const { sessionProbeData, userInfo } = useSelector((state: RootState) => state.data);
  const [navBtnDisable, setNavBtnDisable] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteRecordConfirmation, setShowDeleteRecordConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCounts, setEditCounts] = useState({ beetle: 0, moth: 0, weevil: 0, other: 0 });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isCountExpanded, setIsCountExpanded] = useState(false);
  const [isActionsExpanded, setIsActionsExpanded] = useState(false);

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };

  // Fix hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (sessionProbeData === null) {
      setNavBtnDisable(true);
    } else {
      setNavBtnDisable(false);
    }
    // Reset image error when session data changes
    setImageError(false);
  }, [sessionProbeData]);

  const handleDelete = () => {
    setShowConfirmation(true);
  };

  const confirmDelete = async () => {
    if (sessionProbeData && sessionProbeData[sessionPageNum - 1]) {
      const record = sessionProbeData[sessionPageNum - 1];
      try {
        const result = await dispatch(
          deleteSession({ sessionName: record.session, trapId: record.trapId })
        ).unwrap();
        setShowConfirmation(false);
        resetSessionPage();
        alert(`${t('deleteSuccess')} (${result.deleted} ${t('recordsArchived') || 'records archived'})`);
      } catch {
        alert(t('deleteFailed') || 'Failed to delete session');
      }
    }
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const handleDeleteRecord = () => {
    setShowDeleteRecordConfirmation(true);
  };

  const confirmDeleteRecord = async () => {
    if (sessionProbeData && sessionProbeData[sessionPageNum - 1]) {
      const record = sessionProbeData[sessionPageNum - 1];
      if (!record._id) return;
      try {
        await dispatch(deleteSessionRecord({ recordId: record._id })).unwrap();
        setShowDeleteRecordConfirmation(false);
        if (sessionPageNum > 1 && sessionPageNum >= (sessionProbeData.length)) {
          setSessionPageNum(sessionPageNum - 1);
        }
        alert(t('deleteRecordSuccess') || 'Record deleted successfully');
      } catch {
        alert(t('deleteRecordFailed') || 'Failed to delete record');
      }
    }
  };

  const cancelDeleteRecord = () => {
    setShowDeleteRecordConfirmation(false);
  };

  const handleOpenEditModal = () => {
    if (sessionProbeData && sessionProbeData[sessionPageNum - 1]) {
      const col = sessionProbeData[sessionPageNum - 1].insectCollection;
      setEditCounts({
        beetle: col?.beetle ?? 0,
        moth: col?.moth ?? 0,
        weevil: col?.weevil ?? 0,
        other: col?.other ?? 0,
      });
      setEditError(null);
      setShowEditModal(true);
    }
  };

  const confirmEditCounts = async () => {
    if (!sessionProbeData || !sessionProbeData[sessionPageNum - 1]) return;
    const record = sessionProbeData[sessionPageNum - 1];
    if (!record._id) return;
    for (const val of Object.values(editCounts)) {
      if (val < 0) {
        setEditError(t('editCountsNegativeError') || 'Values must be non-negative');
        return;
      }
    }
    setEditLoading(true);
    setEditError(null);
    try {
      await dispatch(updateInsectData({ recordId: record._id, insectCollection: editCounts })).unwrap();
      setShowEditModal(false);
    } catch (err: unknown) {
      const errData = err as { error?: string } | string;
      const msg =
        typeof errData === 'string'
          ? errData
          : errData?.error || t('editCountsFailed') || 'Failed to update insect data';
      setEditError(msg);
    } finally {
      setEditLoading(false);
    }
  };

  const cancelEditCounts = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  const handlePrevious = () => {
    if (sessionPageNum > 1) {
      setSessionPageNum(sessionPageNum - 1);
    }
  };

  const handleNext = () => {
    if (sessionProbeData && sessionPageNum < sessionProbeData.length) {
      setSessionPageNum(sessionPageNum + 1);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'numeric', day: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(getDateLocale(), { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const convertTemperature = (temp: number | string) => {
    const numTemp = typeof temp === 'string' ? parseFloat(temp) : temp;
    if (isNaN(numTemp)) return '—';
    if (userInfo?.fahrenheit) {
      return ((numTemp * 9 / 5) + 32).toFixed(1) + '°F';
    }
    return numTemp.toFixed(1) + '°C';
  };

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('common:loading')}</h3>
      </div>
    );
  }

  if (!sessionProbeData || sessionProbeData.length === 0 || !sessionProbeData[sessionPageNum - 1]) {
    return (
      <div className="flex items-center justify-center h-full">
        <h3 className="text-gray-800 text-lg">{t('selectSessionToView')}</h3>
      </div>
    );
  }

  const currentData = sessionProbeData[sessionPageNum - 1];

  return (
    <div className="p-5 h-full overflow-y-auto">

      {/* Header Info Bar */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-5 p-4 bg-gray-50 rounded-lg">
        <div>
          <p className="text-sm text-gray-500">{t('trapId')}</p>
          <p className="text-base font-semibold text-gray-900">{currentData.trapId}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">{t('sessionLabel')}</p>
          <p className="text-base font-medium text-gray-900 max-w-[200px] truncate">{currentData.session}</p>
        </div>
        {currentData.location && (
          <div>
            <p className="text-sm text-gray-500">{t('location')}</p>
            <p className="text-base text-gray-900">{currentData.location}</p>
          </div>
        )}
        {currentData.building && (
          <div>
            <p className="text-sm text-gray-500">{t('building')}</p>
            <p className="text-base text-gray-900">{currentData.building}</p>
          </div>
        )}
      </div>

      {/* Image + Data Summary */}
      <div className="flex gap-6 mb-4">

        {/* Left: Circular image + slider navigation */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div className="mb-4">
            {currentData.originalFileUrl && !imageError ? (
              <img
                src={currentData.originalFileUrl}
                alt={t('sessionCapture')}
                className="h-48 w-48 rounded-full object-cover border border-gray-200"
                onError={() => { logger.error('Failed to load image:', currentData.originalFileUrl); setImageError(true); }}
                onLoad={() => setImageError(false)}
              />
            ) : (
              <div className="h-48 w-48 rounded-full bg-gray-200 border border-gray-200 flex items-center justify-center">
                <span className="text-gray-500 text-xs text-center px-4">
                  {imageError ? t('failedToLoadImage') : t('noImageAvailable')}
                </span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrevious}
              disabled={sessionPageNum <= 1}
              className={`px-3 py-1 rounded-md text-sm font-medium cursor-pointer ${
                sessionPageNum <= 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {t('prev')}
            </button>
            <input
              type="range"
              min={1}
              max={sessionProbeData.length}
              value={sessionPageNum}
              onChange={(e) => setSessionPageNum(parseInt(e.target.value))}
              className="w-24 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <button
              onClick={handleNext}
              disabled={sessionPageNum >= sessionProbeData.length}
              className={`px-3 py-1 rounded-md text-sm font-medium cursor-pointer ${
                sessionPageNum >= sessionProbeData.length
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {t('next')}
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {sessionPageNum} of {sessionProbeData.length}
          </p>
        </div>

        {/* Right: Data Summary card */}
        <div className="flex-1 bg-gray-50 rounded-lg p-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900">{t('dataSummary')}</h3>
            <button
              onClick={handleDeleteRecord}
              className="text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
              title={t('deleteRecordAction')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <p className="text-sm text-gray-500">{t('date')}</p>
              <p className="text-base text-gray-900">{formatDate(currentData.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('temperature')}</p>
              <p className="text-base text-gray-900">{convertTemperature(currentData.temperature)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('time')}</p>
              <p className="text-base text-gray-900">{formatTime(currentData.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('humidity')}</p>
              <p className="text-base text-gray-900">{Number(currentData.humidity).toFixed(2)}%</p>
            </div>

            {/* Total Insect Count — expandable, with inline Edit Counts button */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-gray-500">{t('totalCount')}</p>
                <button
                  onClick={handleOpenEditModal}
                  className="text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                  title={t('editCountsAction')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </div>
              <div
                className="p-2 border border-gray-300 rounded-md bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsCountExpanded(!isCountExpanded)}
              >
                <div className="flex justify-between items-center">
                  <span className="text-base text-gray-900">
                    {(currentData.insectCollection?.beetle ?? 0) +
                      (currentData.insectCollection?.moth ?? 0) +
                      (currentData.insectCollection?.weevil ?? 0) +
                      (currentData.insectCollection?.other ?? 0)}{' '}
                    {t('total')}
                  </span>
                  <span className="text-gray-500 text-sm">{isCountExpanded ? '▼' : '▶'}</span>
                </div>
                {isCountExpanded && (
                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                    {([['moth', t('mothCount')], ['beetle', t('beetleCount')], ['weevil', t('weevilCount')], ['other', t('otherCount')]] as [keyof NonNullable<typeof currentData.insectCollection>, string][]).map(([type, label]) => (
                      <div key={type} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">{label}</span>
                        <span className="text-sm font-medium text-gray-900">{currentData.insectCollection?.[type] ?? 0}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Session — bottom left */}
      <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
        <button
          onClick={handleDelete}
          className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          {t('deleteSessionAction')}
        </button>
      </div>

      {/* Delete Session Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('confirmSessionDeletion')}
            </h3>
            <p className="text-gray-900 mb-6">
              {t('deleteConfirmFull')}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer"
              >
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Record Confirmation Modal */}
      {showDeleteRecordConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('confirmRecordDeletion')}
            </h3>
            <p className="text-gray-900 mb-2">
              {t('deleteRecordConfirmMessage')}
            </p>
            <p className="text-sm text-red-600 mb-6">
              {t('cannotBeUndone')}
            </p>
            <div className="flex space-x-4">
              <button
                onClick={cancelDeleteRecord}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmDeleteRecord}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors cursor-pointer"
              >
                {t('deleteRecordButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Insect Counts Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('editCountsTitle')}
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {(['beetle', 'moth', 'weevil', 'other'] as const).map((type) => (
                <div key={type}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 capitalize">
                    {t(`${type}Count`) || type}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={editCounts[type]}
                    onChange={(e) =>
                      setEditCounts((prev) => ({ ...prev, [type]: Math.max(0, Number(e.target.value)) }))
                    }
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={editLoading}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {t('editCountsTotal')}: {editCounts.beetle + editCounts.moth + editCounts.weevil + editCounts.other}
            </p>
            {editError && <p className="text-sm text-red-600 mb-3">{editError}</p>}
            <div className="flex space-x-4">
              <button
                onClick={cancelEditCounts}
                disabled={editLoading}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded hover:bg-gray-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={confirmEditCounts}
                disabled={editLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {editLoading ? t('common:loading') : t('saveButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
